// ChefTabBar — mobile bottom tab strip (May 19 4-button lock).
//
// Ported verbatim from source/screens-tabs.jsx (Desi V2 handoff, 2026-05-19).
// V3 spec refs: Part 6.7.
//
// Current destinations (4 total):
//   • Quotes        — tab-home    (chef's quote list)
//   • Menus         — tab-menus   (chef's menu library)
//   • Distributors  — tab-stack   (navigates to My Stack, the pin-manage view)
//   • Build Quote   — distributor-new  (outlined-orange pill; starts a new quote flow)
//
//   Tab order: Quotes · Menus · Distributors · Build Quote
//   Settings has moved to the profile-badge drawer (not a tab destination).
//   Never adds Discovery / Browse / Marketplace tabs.
//
// Design locks:
//   • "Build Quote" is an OUTLINED-orange pill (not solid) — one-solid-orange-per-screen rule.
//   • Scroll-hide: hides on scroll-down >24px (6px deadzone), reveals on scroll-up.
//
// Translation notes (JSX → TSX):
//   • PhoneShell context (wrapRef.current.closest(".screen")) → adapted to use
//     document.querySelector(".chef-scroller") as the scroll target; parent
//     containers should add className="chef-scroller" to their overflow-auto div.
//   • cls() → inline ternary strings.
//   • CSS vars → FE color constants.

import React, { useRef, useState, useEffect } from 'react';

const C = {
  charcoal: '#2B2B2B',
  orange: '#F2993D',
  gray500: '#6B7280',
  softLine: '#E8E8E8',
} as const;

const serif: React.CSSProperties = {
  fontFamily: "'Playfair Display', Georgia, 'Times New Roman', serif",
};
const sans: React.CSSProperties = {
  fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

type TabId = 'home' | 'menus' | 'order-guides' | 'distributors' | 'settings';

export interface TabDef {
  id: string;
  label: string;
  target: string;
  isAction?: boolean;
}

const CHEF_TABS: TabDef[] = [
  { id: 'home',          label: 'Quotes',       target: 'tab-home' },
  { id: 'menus',         label: 'Menus',        target: 'tab-menus' },
  // B-124: Distributors tab navigates to /chef/distributors (consolidated view).
  { id: 'distributors',  label: 'Distributors', target: 'tab-distributors' },
  { id: 'build',         label: 'Build Quote',  target: 'distributor-new', isAction: true },
];

export interface ChefTabBarProps {
  active?: TabId | string;
  nav?: (target: string) => void;
  /** Override the tab set. Defaults to the chef 4-tab set (Quotes / Menus /
   *  Distributors / Build Quote). Rep mobile nav (Justin's nav ruling,
   *  2026-07) reuses this component's fixed-bottom + scroll-hide chrome with
   *  its own 3-tab set (Today / Inbound / Quotes) rather than a second
   *  parallel bottom-bar implementation — see RepLayout.tsx. */
  tabs?: TabDef[];
}

export function ChefTabBar({ active = 'home', nav = () => {}, tabs = CHEF_TABS }: ChefTabBarProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [hidden, setHidden] = useState(false);

  // Hide on scroll-down, reveal on scroll-up.
  // Verbatim logic from screens-tabs.jsx — finds the nearest .scroller ancestor
  // (prototype used .screen > .scroller; real FE wraps the scroll body in the
  // same component, so we look for the closest .chef-scroller or fall back to
  // the closest overflow-auto ancestor).
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    // Walk up to find a scrollable container. In the FE shell the tab bar sits
    // AFTER the scrollable content div inside the same flex column; use the
    // previous sibling (the scroll body) as the scroll target.
    const scrollTarget =
      el.previousElementSibling instanceof HTMLElement
        ? el.previousElementSibling
        : null;

    if (!scrollTarget) return;

    let lastY = scrollTarget.scrollTop;
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = scrollTarget.scrollTop;
        const dy = y - lastY;
        if (Math.abs(dy) >= 6) {
          if (dy > 0 && y > 24) setHidden(true);
          else if (dy < 0) setHidden(false);
          lastY = y;
        }
        ticking = false;
      });
    };

    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollTarget.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      ref={wrapRef}
      className="flex items-stretch bg-white"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        flex: '0 0 56px',
        borderTop: `1px solid ${C.softLine}`,
        paddingBottom: 12, // clears home indicator on iOS
        transform: hidden ? 'translateY(120%)' : 'translateY(0)',
        transition: 'transform 240ms ease',
        willChange: 'transform',
      }}
    >
      {tabs.map((t) => {
        if (t.isAction) {
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => nav(t.target)}
              className="flex-1 flex items-center justify-center"
              // 44px min tap target — one thumb, wet hands, moving vehicle.
              // The bar's flex: 0 0 56px is inert here (this bar is position:
              // fixed, so it is not a flex item), which is why the row had
              // collapsed to the ~14px text height. Setting the floor on the
              // buttons grows the bar back to its intended ~56px.
              style={{ paddingLeft: 6, paddingRight: 8, minHeight: 44 }}
            >
              <span
                style={{
                  ...sans,
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: C.orange,
                  border: `1.5px solid ${C.orange}`,
                  background: 'transparent',
                  borderRadius: 999,
                  padding: '8px 14px',
                  lineHeight: 1,
                  letterSpacing: 0.1,
                  whiteSpace: 'nowrap',
                }}
              >
                Build Quote
              </span>
            </button>
          );
        }

        const on = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => nav(t.target)}
            className="flex-1 flex flex-col items-center justify-center gap-1"
            // 44px min tap target (see note on the action button above).
            style={{ position: 'relative', minHeight: 44 }}
          >
            <span
              style={{
                ...(on ? serif : sans),
                fontSize: on ? 14 : 13,
                fontWeight: on ? 600 : 500,
                color: on ? C.charcoal : C.gray500,
                letterSpacing: on ? 0 : 0.1,
                lineHeight: 1,
              }}
            >
              {t.label}
            </span>
            {on && (
              <span
                style={{
                  position: 'absolute',
                  top: 4,
                  height: 2,
                  width: 22,
                  background: C.charcoal,
                  borderRadius: 1,
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
